import { createPortal } from "react-dom";
import { SideModal, Button } from "shared";
import { useGetInvestmentTransactions } from "hooks/investments";
import {
  InvestmentTransactionType,
  InvestmentTransactionDetails,
} from "hooks/investments/GetInvestmentTransactionsHook";
import { InvestmentType } from "types/InvestmentTypes";
import TreasuryBondsTable from "./tables/TreasuryBondsTable";
import CryptoTable from "./tables/CryptoTable";
import PreciousStonesTable from "./tables/PreciousStonesTable";
import PreciousMetalsTable from "./tables/PreciousMetalsTable";
import CurrenciesTable from "./tables/CurrenciesTable";
import "./InvestmentTransactionsSideModal.scss";

interface InvestmentTransactionsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
  investmentType: any;
  isOwn?: boolean;
  onAddTransaction?: (type: "buy" | "sell", currentQuantity: number) => void;
  onEditTransaction?: (
    tx: InvestmentTransactionType,
    currentQuantity: number,
  ) => void;
}

// ── slug → renderer map ─────────────────────────────────────────────────────

const renderTableBySlug = (
  slug: string,
  transactions: InvestmentTransactionType[],
  details: InvestmentTransactionDetails | null,
  item: InvestmentType | null,
  isOwn: boolean,
  onEditTx: (tx: InvestmentTransactionType) => void,
) => {
  switch (slug) {
    case "treasury_bonds":
      return (
        <>
          <TreasuryBondsTable
            transactions={transactions}
            details={details}
            isOwn={isOwn}
            onEditTx={onEditTx}
          />
        </>
      );
    case "crypto":
      return (
        <>
          <CryptoTable
            item={item}
            transactions={transactions}
            isOwn={isOwn}
            onEditTx={onEditTx}
          />
        </>
      );
    case "precious_stones":
      return (
        <>
          <PreciousStonesTable
            item={item}
            transactions={transactions}
            isOwn={isOwn}
            onEditTx={onEditTx}
          />
        </>
      );
    case "precious_metals":
      return (
        <>
          <PreciousMetalsTable
            item={item}
            transactions={transactions}
            isOwn={isOwn}
            onEditTx={onEditTx}
          />
        </>
      );
    case "currencies":
      return (
        <>
          <CurrenciesTable
            transactions={transactions}
            isOwn={isOwn}
            onEditTx={onEditTx}
          />
        </>
      );
    default:
      return (
        <p className="investment-transactions-modal__empty">
          Podgląd transakcji dla tego rodzaju aktywa jest niedostępny.
        </p>
      );
  }
};

// ── main component ──────────────────────────────────────────────────────────

const InvestmentTransactionsSideModal: React.FC<
  InvestmentTransactionsSideModalProps
> = ({
  isOpen,
  onClose,
  item,
  investmentType,
  isOwn = false,
  onAddTransaction,
  onEditTransaction,
}) => {
  const { data, isLoading } = useGetInvestmentTransactions(item?.uuid ?? null);

  const transactions = data?.transactions ?? [];
  const details = data?.details ?? null;
  const slug = investmentType?.slug ?? "";

  const totalQuantity = transactions
    .filter((tx: InvestmentTransactionType) => tx.type === "buy")
    .reduce(
      (sum: number, tx: InvestmentTransactionType) => sum + (tx.quantity ?? 0),
      0,
    );
  const soldQuantity = transactions
    .filter((tx: InvestmentTransactionType) => tx.type === "sell")
    .reduce(
      (sum: number, tx: InvestmentTransactionType) => sum + (tx.quantity ?? 0),
      0,
    );
  const currentQuantity = totalQuantity - soldQuantity;

  const currencyCode = item?.currency?.currency ?? "";
  const counterLabel =
    slug === "currencies" ? "Obecna ilość walut:" : "Obecna ilość:";
  const counterValue = isLoading
    ? "..."
    : slug === "currencies"
    ? `${currentQuantity} ${currencyCode}`
    : String(currentQuantity);

  return createPortal(
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Lista transakcji"
      className={`investment-transactions-modal wide ${`investment-transactions-modal--${slug}`}`}
      subtitle={
        <>
          <p>
            <b>Aktywo:</b> {investmentType?.name ?? "—"}
          </p>
          <p className="investment-transactions-modal__counter">
            {counterLabel} <b>{counterValue}</b>
          </p>
        </>
      }
    >
      <Button
        className="button--rounded investment-transactions-modal__button"
        onClick={() =>
          onAddTransaction?.(isOwn ? "buy" : "sell", currentQuantity)
        }
        label="Dodaj transakcję +"
      />
      <div className="investment-transactions-modal__content">
        {isLoading ? (
          <p className="investment-transactions-modal__empty">Ładowanie...</p>
        ) : transactions.length === 0 ? (
          <p className="investment-transactions-modal__empty">
            Brak transakcji dla tego aktywa.
          </p>
        ) : (
          <div className="investment-transactions-modal__table-wrapper">
            {renderTableBySlug(slug, transactions, details, item, isOwn, (tx) =>
              onEditTransaction?.(tx, currentQuantity),
            )}
          </div>
        )}
      </div>
    </SideModal>,
    document.body,
  );
};

export default InvestmentTransactionsSideModal;
